"use client";

import { ArrowRight, Heart, Package, X } from "lucide-react";
import { useState, type FormEvent, type ReactNode } from "react";
import StorefrontAdBanner from "@/components/store/StorefrontAdBanner";
import type { CartItem } from "@/types/cart";
import type { PublicProduct, PublicStore } from "@/types/store";
import RetailHeader from "./RetailHeader";
import RetailFooter from "./RetailFooter";
import RetailProductCard from "./RetailProductCard";
import RetailCategories from "./RetailCategories";
import RetailBenefits from "./RetailBenefits";
import { retailTheme } from "./retail-theme";
import styles from "./retail.module.css";

type Props = {
  store: PublicStore;
  primaryColor: string;
  instagramUrl: string;
  facebookUrl: string;
  formattedCnpj: string;
  attendanceLabel: string;
  contact: ReactNode;
  cart: CartItem[];
  cartItemsCount: number;
  showCart: boolean;
  favoriteProductIds: number[];
  onToggleFavorite: (id: number) => void;
  onOpenProduct: (product: PublicProduct) => void;
  onAdd: (product: PublicProduct) => void;
  onOpenCart: () => void;
  onCampaignProducts: (ids: number[]) => void;
};

export default function RetailStorefront({
  store,
  primaryColor,
  instagramUrl,
  facebookUrl,
  formattedCnpj,
  attendanceLabel,
  cart,
  cartItemsCount,
  showCart,
  favoriteProductIds,
  onToggleFavorite,
  onOpenProduct,
  onAdd,
  onOpenCart,
  onCampaignProducts,
}: Props) {
  const { company, categories } = store;
  const [draftSearch, setDraftSearch] = useState("");
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [subcategoryId, setSubcategoryId] = useState<number | null>(null);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(12);
  const featured = store.products.filter((product) => product.is_featured);
  const selectedCategory = categories.find(
    (category) => category.id === categoryId,
  );
  const query = search.trim().toLocaleLowerCase("pt-BR");
  const products = store.products.filter(
    (product) =>
      (categoryId === null || product.category === categoryId) &&
      (subcategoryId === null || product.subcategory === subcategoryId) &&
      (!favoritesOnly || favoriteProductIds.includes(product.id)) &&
      (!query ||
        [
          product.name,
          product.reference_code,
          product.short_description,
          product.category_name,
          product.subcategory_name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLocaleLowerCase("pt-BR")
          .includes(query)),
  );
  function reset() {
    setDraftSearch("");
    setSearch("");
    setCategoryId(null);
    setSubcategoryId(null);
    setFavoritesOnly(false);
    setVisibleCount(12);
  }
  function scrollToCatalog() {
    requestAnimationFrame(() => {
      document
        .getElementById("catalogo")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }
  function browseCategory(id: number) {
    reset();
    setCategoryId(id);
    setCatalogOpen(true);
    scrollToCatalog();
  }
  function browseCatalog() {
    reset();
    setCatalogOpen(true);
    scrollToCatalog();
  }
  function submitSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSearch(draftSearch);
    setCategoryId(null);
    setSubcategoryId(null);
    setFavoritesOnly(false);
    setCatalogOpen(true);
    setVisibleCount(12);
    scrollToCatalog();
  }
  function cards(items: PublicProduct[], compact = false) {
    return (
      <div className={styles.productGrid}>
        {items.map((product) => (
          <RetailProductCard
            key={product.id}
            product={product}
            compact={compact}
            showCart={showCart}
            quantity={cart
              .filter((item) => item.product.id === product.id)
              .reduce((sum, item) => sum + item.quantity, 0)}
            isFavorite={favoriteProductIds.includes(product.id)}
            onToggleFavorite={() => onToggleFavorite(product.id)}
            onOpen={() => onOpenProduct(product)}
            onAdd={() => onAdd(product)}
            onOpenCart={onOpenCart}
          />
        ))}
      </div>
    );
  }
  function productSection(id: string, title: string, items: PublicProduct[]) {
    if (!items.length) return null;
    return (
      <section
        id={id}
        className={styles.section}
        aria-labelledby={`${id}-title`}
      >
        <div className={styles.sectionHeading}>
          <h2 id={`${id}-title`}>{title}</h2>
          <a href="#catalogo" onClick={browseCatalog}>
            Ver todos <ArrowRight size={16} aria-hidden="true" />
          </a>
        </div>
        {cards(items, id !== "destaques")}
      </section>
    );
  }
  function campaign(
    placement:
      "after_featured" | "between_most_viewed_and_categories" | "before_footer",
  ) {
    return (
      <StorefrontAdBanner
        campaign={store.campaigns?.banners[placement]}
        onProducts={onCampaignProducts}
        className={styles.campaign}
      />
    );
  }
  const hasFilters = Boolean(query || categoryId !== null || favoritesOnly);

  return (
    <div
      className={styles.storefront}
      style={retailTheme(primaryColor)}
      data-storefront-variant="retail"
      id="inicio"
    >
      <RetailHeader
        company={company}
        categories={categories}
        search={draftSearch}
        attendanceLabel={attendanceLabel}
        catalogOpen={catalogOpen}
        categoryId={categoryId}
        onSearchChange={setDraftSearch}
        onSearch={submitSearch}
        onHome={() => {
          reset();
          setCatalogOpen(false);
        }}
        onCatalog={browseCatalog}
        onCategory={browseCategory}
        showCart={showCart}
        cartItemsCount={cartItemsCount}
        onOpenCart={onOpenCart}
      />
      {company.banner_url && (
        <section
          className={styles.hero}
          aria-label={`Banner de ${company.name}`}
        >
          <img
            src={company.banner_url}
            alt={`Banner de ${company.name}`}
            width={1920}
            height={570}
            fetchPriority="high"
            decoding="async"
          />
        </section>
      )}
      <div className={styles.container}>
        {!catalogOpen && (
          <>
            <RetailCategories
              categories={categories}
              products={store.products}
              onCategory={browseCategory}
            />
            {productSection("destaques", "Destaques", featured)}
            {campaign("after_featured")}
            {productSection(
              "mais-acessados",
              "Mais acessados",
              store.most_viewed,
            )}
            {campaign("between_most_viewed_and_categories")}
            {productSection("mais-pedidos", "Mais pedidos", store.best_sellers)}
          </>
        )}
        <section
          className={styles.section}
          hidden={
            !catalogOpen &&
            Boolean(
              featured.length ||
              store.most_viewed.length ||
              store.best_sellers.length,
            )
          }
          id="catalogo"
          aria-labelledby="catalogo-title"
        >
          <div className={styles.sectionHeading}>
            <div>
              <p className={styles.eyebrow}>{company.name}</p>
              <h2 id="catalogo-title">
                {favoritesOnly
                  ? "Favoritos"
                  : selectedCategory?.name || "Produtos"}
              </h2>
            </div>
            <button
              type="button"
              className={styles.textButton}
              aria-pressed={favoritesOnly}
              onClick={() => {
                reset();
                setFavoritesOnly(!favoritesOnly);
                setCatalogOpen(true);
                scrollToCatalog();
              }}
            >
              <Heart size={17} aria-hidden="true" /> Favoritos
            </button>
          </div>
          {selectedCategory && selectedCategory.subcategories.length > 0 && (
            <div className={styles.filters} aria-label="Subcategorias">
              <button
                type="button"
                aria-pressed={subcategoryId === null}
                onClick={() => {
                  setSubcategoryId(null);
                  setVisibleCount(12);
                }}
              >
                Todas
              </button>
              {selectedCategory.subcategories.map((sub) => (
                <button
                  type="button"
                  key={sub.id}
                  aria-pressed={subcategoryId === sub.id}
                  onClick={() => {
                    setSubcategoryId(sub.id);
                    setVisibleCount(12);
                  }}
                >
                  {sub.name}
                </button>
              ))}
            </div>
          )}
          {hasFilters && (
            <div className={styles.results} role="status">
              <p>
                {products.length}{" "}
                {products.length === 1
                  ? "produto encontrado"
                  : "produtos encontrados"}
                {query && ` para “${search.trim()}”`}
              </p>
              <button
                type="button"
                className={styles.textButton}
                onClick={() => {
                  reset();
                  setCatalogOpen(true);
                }}
              >
                <X size={16} aria-hidden="true" /> Limpar filtros
              </button>
            </div>
          )}
          {products.length ? (
            cards(products.slice(0, visibleCount))
          ) : (
            <div className={styles.empty}>
              <Package size={36} strokeWidth={1} aria-hidden="true" />
              <h3>Nenhum produto encontrado</h3>
              {hasFilters && <p>Tente outro termo ou limpe os filtros.</p>}
            </div>
          )}
          {products.length > visibleCount && (
            <div className={styles.loadMore}>
              <button
                type="button"
                onClick={() => setVisibleCount((count) => count + 12)}
              >
                Carregar mais produtos
              </button>
            </div>
          )}
        </section>
        {campaign("before_footer")}
      </div>
      {company.description && (
        <section id="sobre" className={styles.about}>
          <div className={`${styles.container} ${styles.aboutGrid}`}>
            <div>
              <p className={styles.eyebrow}>Conheça a loja</p>
              <h2>{company.name}</h2>
            </div>

            <div>
              <p className={styles.aboutDescription}>{company.description}</p>
            </div>
          </div>
        </section>
      )}

      <RetailBenefits company={company} attendanceLabel={attendanceLabel} />
      <RetailFooter
        onHome={() => {
          reset();
          setCatalogOpen(false);
        }}
        onCatalog={browseCatalog}
        company={company}
        formattedCnpj={formattedCnpj}
        instagramUrl={instagramUrl}
        facebookUrl={facebookUrl}
      />
    </div>
  );
}
